import { useState, useEffect, useRef, useMemo } from "react";
import { Pencil } from "lucide-react";
import usePersonaStore from "../../Store/PersonaStore.jsx";
import {
  GENDER_OPTIONS,
  PERSONALITY_TRAITS,
  INTERESTS,
  fileToBase64,
  isUserAuthenticated
} from "../../Utils/personaUtils.js";
import { storePendingPersonaData } from "../../Services/personaService.js";
import Button from "../ui/Button/Button.jsx";
import IconButton from "../ui/Button/IconButton.jsx";
import {
  FormModal,
  Form,
  SmartFormField,
  SmartPillGroup,
  SmartControl,
  FormSection,
  FormInput,
  FormLabel,
  SubmitButton,
  PillGroup
} from "../ui/Form";
import { personaSchema } from "../../schemas/personaSchema.js";
import { useTranslation } from "react-i18next";

export default function PersonaForm() {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Get store functions and data
  const { personaFormData, closePersonaForm, createPersona } = usePersonaStore();

  // Prepare default values
  const defaultValues = {
    name: personaFormData?.name || "",
    age: personaFormData?.age ? String(personaFormData.age) : "",
    gender: personaFormData?.gender || "",
    personalityTraits: personaFormData?.personality || [],
    interests: personaFormData?.hobbies || [],
    image: null
  };

  // Mapped options for translation display
  const genderOptions = useMemo(() => [
    { value: "Male", label: t("gender.male") },
    { value: "Female", label: t("gender.female") },
    { value: "Non Binary", label: t("gender.nonBinary") }
  ], [t]);

  const personalityOptions = useMemo(() => PERSONALITY_TRAITS.map(trait => {
    // extract key from trait (e.g. "🤝 Friendly" -> "friendly")
    const key = trait.split(" ")[1]?.toLowerCase();
    return { value: trait, label: t(`personalityTraits.${key}`, { defaultValue: trait }) };
  }), [t]);

  const interestOptions = useMemo(() => INTERESTS.map(interest => {
    // simple mapping for interests
    const mapping = {
      "🎨 Art and Crafting": "art",
      "🎮 Gaming": "gaming",
      "📚 Reading": "reading",
      "🎵 Music": "music",
      "🎥 Movies and TV Shows": "movies",
      "🏋️‍♂️ Fitness and Exercise": "fitness",
      "🍳 Cooking and Baking": "cooking",
      "🌍 Traveling": "traveling",
      "📸 Photography": "photography",
      "🌱 Gardening": "gardening",
      "🐾 Pets and Animal Care": "pets",
      "🚴‍♂️ Outdoor Activities": "outdoor",
      "🧩 Puzzles and Board Games": "puzzles",
      "🛠️ DIY Projects": "diy",
      "✍️ Writing or Journaling": "writing",
      "🧘‍♀️ Meditation and Yoga": "meditation",
      "⚽ Sports": "sports",
      "💻 Technology and Gadgets": "tech",
      "🏖️ Beach and Water Activities": "beach"
    };
    const key = mapping[interest];
    return { value: interest, label: t(`interestsList.${key}`, { defaultValue: interest }) };
  }), [t]);

  // Auto-expand advanced fields if personality or hobbies are provided
  useEffect(() => {
    if (personaFormData) {
      if ((personaFormData.personality && personaFormData.personality.length > 0) ||
        (personaFormData.hobbies && personaFormData.hobbies.length > 0)) {
        setShowAdvanced(true);
      }
    }
  }, [personaFormData]);

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (data, { setFormError }) => {
    // Prepare persona data in the required format
    const personaData = {
      image: data.image,
      name: data.name,
      age: data.age,
      gender: data.gender,
      personalityTraits: data.personalityTraits,
      interests: data.interests,
    };

    if (!isUserAuthenticated()) {
      storePendingPersonaData(personaData);
      window.location.href = "/login";
    } else {
      const response = await createPersona(personaData);

      if (response.success) {
        closePersonaForm();
      } else {
        throw new Error(response.message || "Failed to create persona");
      }
    }
  };

  return (
    <FormModal
      isOpen={true}
      onClose={closePersonaForm}
      title={t("shopify.createNewPersonaTitle", { defaultValue: "Create New Persona" })}
    >
      <Form
        schema={personaSchema}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        className="space-y-4"
      >
        {/* Profile Image */}
        <SmartControl
          name="image"
          label={t("profile.profileImage")}
          render={({ value, onChange }) => (
            <div className="flex justify-center items-center">
              <div className="relative">
                {value || imagePreview ? (
                  <img
                    src={value || imagePreview}
                    alt="Profile"
                    className="w-[70px] h-[70px] rounded-full object-cover"
                  />
                ) : (
                  <div className="w-[70px] h-[70px] rounded-full bg-gradient-to-b from-[rgba(173,23,0,0.53)] to-[rgba(8,143,145,0.44)]" />
                )}
                <IconButton
                  icon={<Pencil className="w-4 h-4" />}
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-0 right-0 bg-grey-500 rounded-full p-2"
                  onClick={triggerFileInput}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        const base64 = await fileToBase64(file);
                        setImagePreview(base64);
                        onChange(base64);
                      } catch (error) {
                        console.error("Error converting image:", error);
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}
        />

        {/* Name and Age Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <SmartFormField
            name="name"
            label={t("eventsAndSpaces.name")}
            required
            inputProps={{
              type: "text",
              placeholder: t("persona.placeholderName", { defaultValue: "Persona name" })
            }}
          />

          <SmartControl
            name="age"
            label={t("profile.age")}
            render={({ value, onChange, error }) => (
              <div className="flex flex-col gap-1">
                <FormLabel required>{t("profile.age")}</FormLabel>
                <div className="relative">
                  <FormInput
                    type="text"
                    placeholder={t("shopify.placeholderAge", { defaultValue: "Enter age" })}
                    value={value}
                    className="pr-20"
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || (/^\d+$/.test(val) && parseInt(val) >= 1 && parseInt(val) <= 200)) {
                        onChange(val);
                      }
                    }}
                    error={!!error}
                  />
                  {value && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500">{t("gender.yearsOld")}</span>
                    </div>
                  )}
                </div>
                {error && <span className="text-red-500 text-xs">{t(error, { defaultValue: error })}</span>}
              </div>
            )}
          />
        </div>

        {/* Gender Selection */}
        <div className="flex flex-col gap-2.5">
          <FormLabel required>{t("profile.gender")}</FormLabel>
          <SmartPillGroup
            name="gender"
            label={t("profile.gender")}
            options={genderOptions}
            size="xs2"
          />
        </div>

        {/* Advanced Fields Section */}
        <FormSection
          title={showAdvanced ? t("eventsAndSpaces.hideOptionalFields") : t("eventsAndSpaces.showOptionalFields")}
          collapsible
          isOpen={showAdvanced}
          onToggle={setShowAdvanced}
        >
          {/* Personality Traits */}
          <SmartControl
            name="personalityTraits"
            label={t("profile.personality")}
            render={({ value, onChange }) => (
              <div className="flex flex-col gap-2.5 pt-3 px-2">
                <FormLabel>{t("profile.personality")}</FormLabel>
                <PillGroup
                  options={[
                    ...value.filter(trait => !PERSONALITY_TRAITS.includes(trait)).map(trait => ({ value: trait, label: trait })),
                    ...personalityOptions
                  ]}
                  selected={value}
                  onSelect={onChange}
                  multiple
                  size="xs2"
                />
              </div>
            )}
          />

          {/* Interests and Hobbies */}
          <SmartControl
            name="interests"
            label={t("profile.interests")}
            render={({ value, onChange }) => (
              <div className="flex flex-col gap-2.5 pt-3 px-2">
                <FormLabel>{t("profile.interests")}</FormLabel>
                <PillGroup
                  options={[
                    ...value.filter(interest => !INTERESTS.includes(interest)).map(interest => ({ value: interest, label: interest })),
                    ...interestOptions
                  ]}
                  selected={value}
                  onSelect={onChange}
                  multiple
                  size="xs2"
                />
              </div>
            )}
          />
        </FormSection>

        {/* Submit Button */}
        <SubmitButton
          variant="primary"
          size="lg"
          rounded="full"
          fullWidth
          className="mt-5"
        >
          {t("profile.createPersonaBtn")}
        </SubmitButton>
      </Form>
    </FormModal>
  );
}